import { useMemo, useRef } from 'react';
import { FixedSizeList } from 'react-window';
import type { ListChildComponentProps } from 'react-window';
import { redact } from '@/utils/redaction';
import { formatTime } from '@/utils/format';
import type { EventDto } from '@/types/api';

const ROW_HEIGHT = 28;
const LIST_HEIGHT = 460;
const MAX_LOG_EVENTS = 1000;

const LOG_GRID = 'grid grid-cols-[92px_90px_190px_100px_90px_150px_1fr] gap-3 px-4';
const LOG_COLUMNS = ['Time', 'Service', 'Event', 'Actor', 'Role', 'Message', 'Details'] as const;

export interface VirtualLogListProps {
  events: ReadonlyArray<EventDto>;
  searchQuery: string;
}

export function VirtualLogList({ events, searchQuery }: VirtualLogListProps): JSX.Element {
  const visible = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    const ordered = [...events].sort((a, b) => {
      const ta = Date.parse(a.emitted_at);
      const tb = Date.parse(b.emitted_at);
      if (tb !== ta) return tb - ta;
      return b.event_id.localeCompare(a.event_id);
    });
    const redacted = ordered.map((e) => {
      const redactedDetails = redact(e.details);
      return {
        ...e,
        details: redactedDetails,
        _detailsText: stringifyDetails(redactedDetails)
      };
    });
    if (q === '') return { rows: redacted, matchCount: 0 };
    let matchCount = 0;
    const rows = redacted.filter((e) => {
      const haystack =
        `${e.service} ${e.event_type} ${e.actor} ${e.actor_email ?? ''} ${e.actor_role ?? ''} ${e.message ?? ''} ${e._detailsText}`.toLowerCase();
      if (haystack.includes(q)) {
        matchCount += 1;
        return true;
      }
      return false;
    });
    return { rows, matchCount };
  }, [events, searchQuery]);

  const itemData = useMemo(
    () => ({ rows: visible.rows, query: searchQuery.trim() }),
    [visible.rows, searchQuery]
  );

  const listRef = useRef<FixedSizeList>(null);

  return (
    <div data-testid="virtual-log-list">
      <div
        className={`${LOG_GRID} py-2 border-b border-border bg-panel-2 text-[10.5px] uppercase tracking-wide text-text-muted font-medium`}
        data-testid="log-list-header"
      >
        {LOG_COLUMNS.map((label) => (
          <span key={label} className="truncate">
            {label}
          </span>
        ))}
      </div>
      <FixedSizeList
        ref={listRef}
        height={LIST_HEIGHT}
        itemCount={visible.rows.length}
        itemSize={ROW_HEIGHT}
        width="100%"
        itemData={itemData}
      >
        {LogRow}
      </FixedSizeList>
      <div
        className="px-4 py-2 border-t border-border text-[11px] text-text-muted flex justify-between"
        data-testid="log-list-footer"
      >
        <div>
          Showing {visible.rows.length} of {events.length} events
          {
}
          {events.length >= MAX_LOG_EVENTS ? ' (most recent · server cap reached)' : ''}
          {searchQuery.trim() ? ` · ${visible.matchCount} match${visible.matchCount === 1 ? '' : 'es'}` : ''}
          {' · redaction applied at display boundary'}
        </div>
      </div>
    </div>
  );
}

interface RowData {
  rows: ReadonlyArray<EventDto & { _detailsText: string }>;
  query: string;
}

function LogRow(props: ListChildComponentProps<RowData>): JSX.Element {
  const { index, style, data } = props;
  const row = data.rows[index];
  if (!row) return <div style={style} />;
  const detailText = collapseWhitespace(row._detailsText);
  const role = row.actor_role ?? '';
  const message = row.message ?? '';
  const actor = row.actor_email ?? row.actor;
  return (
    <div
      style={style}
      className={`${LOG_GRID} border-b border-border font-mono text-[11.5px] hover:bg-panel-2 items-center`}
      data-testid={`log-row-${row.event_id}`}
    >
      <span className="text-[#9CA3AF] tabular-nums truncate">{formatTime(row.emitted_at)}</span>
      <span className="text-text truncate">{row.service}</span>
      <span className="text-[#2563c9] truncate">
        {highlight(row.event_type, data.query)}
      </span>
      <span className="text-text-muted truncate" title={actor}>
        {highlight(actor, data.query)}
      </span>
      <span className="text-text truncate" title={role}>
        {role ? highlight(role, data.query) : '—'}
      </span>
      <span className="text-text truncate" title={message}>
        {message ? highlight(message, data.query) : '—'}
      </span>
      <span className="text-text-muted truncate" title={detailText}>
        {highlight(detailText, data.query)}
      </span>
    </div>
  );
}

function stringifyDetails(d: Record<string, unknown>): string {
  return Object.entries(d)
    .map(([k, v]) => `${k}=${typeof v === 'object' ? JSON.stringify(v) : String(v)}`)
    .join(' ');
}

function collapseWhitespace(text: string): string {
  return text.replace(/\s+/g, ' ').trim();
}

function highlight(text: string, query: string): JSX.Element | string {
  if (!query) return text;
  const lower = text.toLowerCase();
  const q = query.toLowerCase();
  const parts: Array<JSX.Element | string> = [];
  let i = 0;
  let key = 0;
  while (i < text.length) {
    const idx = lower.indexOf(q, i);
    if (idx === -1) {
      parts.push(text.slice(i));
      break;
    }
    if (idx > i) parts.push(text.slice(i, idx));
    parts.push(
      <mark
        key={key++}
        className="bg-warn/35 text-text rounded-sm px-0.5"
      >
        {text.slice(idx, idx + q.length)}
      </mark>
    );
    i = idx + q.length;
  }
  return <>{parts}</>;
}
